import { useTranslation } from 'react-i18next';
import { ScrollArea, Stack, Table, Text } from '@mantine/core';
import DocRow from '@/components/app/deals/steps/forwarding/DocRow';
import StageSection from '@/components/app/deals/steps/forwarding/StageSection';
import { countVehiclesAllocated, remaining } from '@/lib/forwardingView';
import { fmtFwdDate } from '@/lib/forwarding';
import { formatMoney } from '@/lib/format';
import type { ForwardingInfo } from '@/types/deals';
import NumCell from '@/components/common/NumCell';

interface AllocatedWeightProps {
  readonly info: ForwardingInfo;
  /** Дневная мощность отгрузки в тоннах (X авто × 30 / X т) — план «на день». */
  readonly dailyCapacityTon?: number;
  /** Общий объём товара сделки (т) — потолок дневного плана. */
  readonly dealVolumeTon?: number;
}

// Этап «Разобранный вес»: план перевозки вперёд по дням (РТС) — вес, который ТС пообещали забрать.
// Таблица по дням (хронологически): дата · документ РТС · ТС · разобрано · осталось. Список машин —
// внутри документа РТС. «Разобрано» дня = ТС × 30 т (= disassembledWeightTon).
// «Осталось» дня (КУМУЛЯТИВНО) = планДня − разобрано, где планДня = min(дневная мощность отгрузки,
// ОСТАТОК объёма сделки на начало дня); остаток уменьшается на разобранное по предыдущим дням.
// Вес в тоннах, 1 ТС ≈ 30 т.
export default function AllocatedWeight({ info, dailyCapacityTon, dealVolumeTon }: AllocatedWeightProps) {
  const { t } = useTranslation();
  const planned = info.plannedDeliveryInfo;
  const days = planned?.deliveryDays ?? [];

  if (!days.length && !planned?.weightTon) {
    return null;
  }

  // Дни — в хронологическом порядке (кумулятивный остаток зависит от последовательности).
  const orderedDays = [...days].sort((a, b) => (a.deliveryDate ?? '').localeCompare(b.deliveryDate ?? ''));

  // Кумулятивный «осталось» по дням: планДня = min(мощность, остаток объёма сделки), затем остаток
  // уменьшается на разобранное. null → данных мощности/объёма нет, деградируем к weightTon−disassembled.
  const dayRemainingTon: number[] | null = (() => {
    if (dailyCapacityTon == null || dealVolumeTon == null) {
      return null;
    }
    let left = dealVolumeTon;
    return orderedDays.map((day) => {
      const allocated = day.disassembledWeightTon ?? 0;
      const planDay = Math.min(dailyCapacityTon, left);
      const rest = Math.max(0, planDay - allocated);
      left = Math.max(0, left - allocated);
      return rest;
    });
  })();

  return (
    <StageSection
      title={t('app.deals.forwarding.allocated')}
      unit={t('app.deals.tons')}
      doneLabel={t('app.deals.forwarding.allocatedDone')}
      done={planned?.disassembledWeightTon}
      total={planned?.weightTon}
      remainingLabel={t('app.deals.forwarding.allocatedRemaining')}
      remaining={planned?.remainingWeightTon}
      count={countVehiclesAllocated(info)}
      countLabel={t('app.deals.forwarding.vehiclesShort')}
      emptyText={days.length ? undefined : t('app.deals.forwarding.allocatedEmpty')}
      compact
    >
      <Stack gap="xs">
        <Text fz="xs" c="gray.6">
          {t('app.deals.forwarding.allocatedHint')}
        </Text>
        <ScrollArea.Autosize mah={320} type="auto">
          <Table stickyHeader highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('app.deals.forwarding.colDate')}</Table.Th>
                <Table.Th>{t('app.deals.forwarding.colDoc')}</Table.Th>
                <Table.Th ta="right">{t('app.deals.forwarding.vehiclesShort')}</Table.Th>
                <Table.Th ta="right">{t('app.deals.forwarding.colAllocated')}</Table.Th>
                <Table.Th ta="right">{t('app.deals.forwarding.colRemaining')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {orderedDays.map((day, i) => (
                <Table.Tr key={day.deliveryDate ?? i}>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    {fmtFwdDate(day.deliveryDate) ?? '—'}
                  </Table.Td>
                  <Table.Td>
                    {day.truncatedRts ? (
                      <DocRow doc={day.truncatedRts} />
                    ) : (
                      <Text fz="xs" c="gray.5">
                        {t('app.deals.forwarding.rtsAbsent')}
                      </Text>
                    )}
                  </Table.Td>
                  <NumCell ta="right">{day.numVehicles ?? 0}</NumCell>
                  <NumCell ta="right" style={{ whiteSpace: 'nowrap' }}>{formatMoney(day.disassembledWeightTon)} {t('app.deals.tons')}</NumCell>
                  <NumCell ta="right" style={{ whiteSpace: 'nowrap' }}>{formatMoney(dayRemainingTon?.[i] ?? remaining(day.weightTon, day.disassembledWeightTon))}{' '} {t('app.deals.tons')}</NumCell>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      </Stack>
    </StageSection>
  );
}
